/**
 * Currency utilities for configurable default currency.
 * Default is always EUR if nothing else is configured.
 */

/** Mapping country code → ISO 4217 currency code */
export const COUNTRY_CURRENCY_MAP: Record<string, string> = {
  DE: 'EUR', AT: 'EUR', BE: 'EUR', CY: 'EUR', EE: 'EUR',
  ES: 'EUR', FI: 'EUR', FR: 'EUR', GR: 'EUR', HR: 'EUR',
  IE: 'EUR', IT: 'EUR', LT: 'EUR', LU: 'EUR', LV: 'EUR',
  MT: 'EUR', NL: 'EUR', PT: 'EUR', SI: 'EUR', SK: 'EUR',
  CH: 'CHF', GB: 'GBP', US: 'USD', TH: 'THB',
  CZ: 'CZK', DK: 'DKK', HU: 'HUF', PL: 'PLN',
  SE: 'SEK', BG: 'BGN', RO: 'RON',
};

/** Supported currencies for the settings dropdown */
export const CURRENCY_OPTIONS = [
  { code: 'EUR', name: 'Euro (€)', symbol: '€' },
  { code: 'USD', name: 'US Dollar ($)', symbol: '$' },
  { code: 'GBP', name: 'Britisches Pfund (£)', symbol: '£' },
  { code: 'CHF', name: 'Schweizer Franken (CHF)', symbol: 'CHF' },
  { code: 'THB', name: 'Thai Baht (฿)', symbol: '฿' },
  { code: 'CZK', name: 'Tschechische Krone (Kč)', symbol: 'Kč' },
  { code: 'DKK', name: 'Dänische Krone (kr)', symbol: 'kr' },
  { code: 'HUF', name: 'Ungarischer Forint (Ft)', symbol: 'Ft' },
  { code: 'PLN', name: 'Polnischer Zloty (zł)', symbol: 'zł' },
  { code: 'SEK', name: 'Schwedische Krone (kr)', symbol: 'kr' },
  { code: 'BGN', name: 'Bulgarischer Lew (лв)', symbol: 'лв' },
  { code: 'RON', name: 'Rumänischer Leu (lei)', symbol: 'lei' },
];

/** Auswählbare Länder (Code + deutscher Name) — deckt COUNTRY_CURRENCY_MAP ab.
 * Gemeinsame Quelle für Registrierung und Firmeneinstellungen. */
export const COUNTRY_OPTIONS = [
  { code: 'DE', name: 'Deutschland' }, { code: 'AT', name: 'Österreich' },
  { code: 'CH', name: 'Schweiz' }, { code: 'TH', name: 'Thailand' },
  { code: 'NL', name: 'Niederlande' }, { code: 'BE', name: 'Belgien' },
  { code: 'FR', name: 'Frankreich' }, { code: 'IT', name: 'Italien' },
  { code: 'ES', name: 'Spanien' }, { code: 'PT', name: 'Portugal' },
  { code: 'IE', name: 'Irland' }, { code: 'LU', name: 'Luxemburg' },
  { code: 'FI', name: 'Finnland' }, { code: 'GR', name: 'Griechenland' },
  { code: 'HR', name: 'Kroatien' }, { code: 'SI', name: 'Slowenien' },
  { code: 'SK', name: 'Slowakei' }, { code: 'EE', name: 'Estland' },
  { code: 'LT', name: 'Litauen' }, { code: 'LV', name: 'Lettland' },
  { code: 'CY', name: 'Zypern' }, { code: 'MT', name: 'Malta' },
  { code: 'GB', name: 'Vereinigtes Königreich' }, { code: 'US', name: 'USA' },
  { code: 'CZ', name: 'Tschechien' }, { code: 'DK', name: 'Dänemark' },
  { code: 'HU', name: 'Ungarn' }, { code: 'PL', name: 'Polen' },
  { code: 'SE', name: 'Schweden' }, { code: 'BG', name: 'Bulgarien' },
  { code: 'RO', name: 'Rumänien' },
];

/** Get the default currency for a country code */
export function getCurrencyForCountry(countryCode: string): string {
  return COUNTRY_CURRENCY_MAP[countryCode?.toUpperCase()] || 'EUR';
}

/** Get the symbol for a currency code */
export function getCurrencySymbol(currencyCode: string): string {
  const option = CURRENCY_OPTIONS.find(c => c.code === currencyCode);
  if (option) return option.symbol;
  // Fallback: use Intl
  try {
    const parts = new Intl.NumberFormat('de-DE', { style: 'currency', currency: currencyCode }).formatToParts(0);
    const symbolPart = parts.find(p => p.type === 'currency');
    return symbolPart?.value || currencyCode;
  } catch {
    return currencyCode;
  }
}

/** Format an amount with the given currency using Intl.NumberFormat */
export function formatAmount(amount: number, currency = 'EUR', locale = 'de-DE'): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    // 🚨 **Auch der Rueckfall spricht die Sprache des Mandanten.** `Intl.NumberFormat` wirft nur
    // bei einem Waehrungscode, den es nicht kennt — die ZAHL ist davon unberuehrt. Mit
    // `toFixed(2)` stand hier vorher `1234.50 XYZ`; jetzt bleibt es `1.234,50 XYZ`.
    const zahl = amount.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return `${zahl} ${currency}`;
  }
}
